/**
 * Deposit Contracts - Zod schemas matching PostgreSQL exactly
 *
 * DATABASE-FIRST: All schemas derived from MCP postgres queries on 2026-01-02
 *
 * Table: deposits (14 columns)
 * Key functions: create_deposit_v2, get_deposit_by_ref, log_deposit_event_v2,
 *   calculate_bonus_for_deposit
 */
import { z } from 'zod';
import { bigintString, numericString, timestampString, depositStatusSchema, depositReferenceSchema, depositLogTypeSchema, successResponseSchema, } from '../common.js';
// =============================================================================
// DATABASE ENTITY: deposits table
// Source: SELECT * FROM information_schema.columns WHERE table_name = 'deposits'
// =============================================================================
/**
 * Full deposit record as stored in PostgreSQL
 * Matches get_deposit_by_ref() return type EXACTLY
 */
export const depositEntitySchema = z.object({
    reference_number: z.string(), // varchar NOT NULL (PK)
    user_telegram_id: bigintString, // bigint NOT NULL → string in JSON
    amount: z.number().int(), // integer NOT NULL
    bonus_amount: z.number().int().nullable(), // integer NULL (default 0)
    bonus_percentage: z.number().int().nullable(), // integer NULL (default 0)
    status: depositStatusSchema, // varchar NOT NULL (CHECK constraint)
    completed_at: timestampString, // timestamptz NULL
    provider_hash: z.string().nullable(), // varchar NULL
    provider_transaction_id: z.string().nullable(), // varchar NULL
    usdt_amount: numericString, // numeric NULL → string in JSON
    exchange_rate: numericString, // numeric NULL → string in JSON
    commission: numericString, // numeric NULL → string in JSON
    photo_url: z.string().nullable(), // text NULL
    created_at: timestampString, // timestamptz NULL (default now())
});
// =============================================================================
// STORED PROCEDURE: create_deposit_v2
// Args: p_telegram_id bigint, p_amount integer
// Returns: TABLE(success, message, reference_number, bonus_percentage, bonus_amount)
// =============================================================================
export const createDepositInputSchema = z.object({
    telegram_id: bigintString,
    amount: z.number().int().positive(),
});
export const createDepositResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    reference_number: z.string(),
    bonus_percentage: z.number().int(),
    bonus_amount: z.number().int(),
});
// =============================================================================
// STORED PROCEDURE: calculate_bonus_for_deposit
// Args: p_telegram_id bigint, p_deposit_amount integer
// Returns: TABLE(bonus_percentage integer, bonus_amount integer)
// =============================================================================
export const calculateBonusInputSchema = z.object({
    telegram_id: bigintString,
    deposit_amount: z.number().int().positive(),
});
export const calculateBonusResultSchema = z.object({
    bonus_percentage: z.number().int(),
    bonus_amount: z.number().int(),
});
// =============================================================================
// STORED PROCEDURE: log_deposit_event_v2
// Args: p_deposit_ref, p_log_type, [optional params...]
// Returns: TABLE(success boolean, message text, log_id integer)
// =============================================================================
export const logDepositEventInputSchema = z.object({
    deposit_ref: depositReferenceSchema,
    log_type: depositLogTypeSchema,
    cascade_stats: z.record(z.unknown()).nullable().optional(),
    api_response: z.record(z.unknown()).nullable().optional(),
    provider_hash: z.string().nullable().optional(),
    provider_transaction_id: z.string().nullable().optional(),
    photo_url: z.string().nullable().optional(),
    staff_id: z.string().nullable().optional(),
    note: z.string().nullable().optional(),
    previous_values: z.record(z.unknown()).nullable().optional(),
    new_values: z.record(z.unknown()).nullable().optional(),
    result: z.string().nullable().optional(),
});
export const logDepositEventResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    log_id: z.number().int(),
});
// =============================================================================
// API REQUESTS
// =============================================================================
/**
 * POST /api/wallet/deposit
 * Request body for initiating a deposit
 */
export const depositRequestSchema = z.object({
    amount: z.number().int().positive('Amount must be a positive integer'),
});
/**
 * POST /api/wallet/deposit/:referenceNumber/receipt
 * Request body for submitting receipt
 */
export const depositReceiptRequestSchema = z.object({
    fileId: z.string().min(1, 'fileId is required'),
});
// =============================================================================
// API RESPONSES
// =============================================================================
/**
 * POST /api/wallet/deposit - Success response
 * Returns card details or payment URL
 */
export const depositInitResponseDataSchema = z.object({
    referenceNumber: depositReferenceSchema,
    dealId: z.string(),
    method: z.string(),
    amount: z.number().int(),
    bonusPercentage: z.number().int(),
    bonusAmount: z.number().int(),
    cardNumber: z.string().nullable(),
    cardHolderName: z.string().nullable(),
    bank: z.string().nullable(),
    paymentUrl: z.string().nullable(),
    expiresAt: z.string().datetime(),
});
export const depositInitResponseSchema = successResponseSchema(depositInitResponseDataSchema);
/**
 * GET /api/wallet/deposit/pending - Success response (has pending)
 */
export const pendingDepositInfoSchema = z.object({
    referenceNumber: depositReferenceSchema,
    status: z.enum(['pending', 'paid']), // Only these two can be "pending"
    amount: z.number().int(),
    bonusAmount: z.number().int(),
    bonusPercentage: z.number().int(),
    dealId: z.string().nullable(),
    cardNumber: z.string().nullable(),
    cardHolderName: z.string().nullable(),
    bank: z.string().nullable(),
    createdAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
});
export const pendingDepositResponseDataSchema = z.discriminatedUnion('hasPending', [
    z.object({
        hasPending: z.literal(false),
    }),
    z.object({
        hasPending: z.literal(true),
        deposit: pendingDepositInfoSchema,
    }),
]);
export const pendingDepositResponseSchema = successResponseSchema(pendingDepositResponseDataSchema);
/**
 * GET /api/wallet/deposit/:referenceNumber/status
 */
export const depositStatusResponseDataSchema = z.object({
    referenceNumber: depositReferenceSchema,
    status: depositStatusSchema,
    amount: z.number().int(),
    bonusAmount: z.number().int(),
    createdAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
});
export const depositStatusResponseSchema = successResponseSchema(depositStatusResponseDataSchema);
/**
 * POST /api/wallet/deposit/:referenceNumber/check
 */
export const depositCheckResponseDataSchema = z.object({
    status: depositStatusSchema,
    action: z.enum([
        'payment_confirmed',
        'still_pending',
        'already_complete',
        'no_deal_id',
    ]),
    message: z.string(),
    providerStatus: z.string().optional(),
    depositAmount: z.number().int().optional(),
    bonusAmount: z.number().int().optional(),
});
export const depositCheckResponseSchema = successResponseSchema(depositCheckResponseDataSchema);
/**
 * POST /api/wallet/deposit/:referenceNumber/receipt - Success
 */
export const depositReceiptResponseDataSchema = z.object({
    status: z.literal('completed'),
    message: z.string(),
    depositAmount: z.number().int(),
    bonusAmount: z.number().int(),
    totalCredited: z.number().int(),
});
export const depositReceiptResponseSchema = successResponseSchema(depositReceiptResponseDataSchema);
/**
 * POST /api/wallet/deposit/:referenceNumber/cancel - Success
 */
export const depositCancelResponseDataSchema = z.object({
    message: z.string(),
});
export const depositCancelResponseSchema = successResponseSchema(depositCancelResponseDataSchema);
// =============================================================================
// ERROR RESPONSES
// =============================================================================
/**
 * POST /api/wallet/deposit - Error: pending exists
 */
export const depositPendingExistsErrorSchema = z.object({
    success: z.literal(false),
    error: z.string(),
    data: z.object({
        referenceNumber: depositReferenceSchema,
        dealId: z.string().nullable(),
    }),
});
/**
 * POST /api/wallet/deposit - Error: amount too low
 */
export const depositAmountTooLowErrorSchema = z.object({
    success: z.literal(false),
    error: z.string(),
    data: z.object({
        minimumAmount: z.number().int(),
    }),
});
/**
 * POST /api/wallet/deposit - Error: no providers
 */
export const depositNoProvidersErrorSchema = z.object({
    success: z.literal(false),
    error: z.string(),
    data: z.object({
        attempts: z.array(z.object({
            method: z.string(),
            result: z.enum(['failed', 'skipped']),
            error: z.string().optional(),
        })),
    }),
});
// =============================================================================
// POLLING CONFIGURATION
// =============================================================================
/**
 * Polling configuration for deposit status checks
 *
 * Uses exponential backoff to balance responsiveness vs server load:
 * - Starts fast (3s intervals) for immediate feedback
 * - Gradually slows down (1.1x multiplier) if user is waiting longer
 * - Caps at maxIntervalMs to prevent excessive slowdown
 * - Stops after maxAttempts to prevent infinite polling
 */
export const depositPollingConfigSchema = z.object({
    /** Base polling interval in milliseconds */
    intervalMs: z.number().int().min(1000).default(3000),
    /** Maximum number of poll attempts before giving up */
    maxAttempts: z.number().int().min(1).default(200),
    /** Multiplier applied to interval after each poll (exponential backoff) */
    backoffMultiplier: z.number().min(1).default(1.1),
    /** Maximum interval cap in milliseconds (prevents intervals from growing too large) */
    maxIntervalMs: z.number().int().default(10000),
});
/**
 * Default polling configuration
 *
 * Coverage: ~32 minutes total polling time
 * - First 13 polls: ~73s (ramping from 3s to 10s intervals)
 * - Remaining 187 polls: ~31 min at 10s intervals
 */
export const DEFAULT_DEPOSIT_POLLING = {
    intervalMs: 3000, // Start at 3 seconds
    maxAttempts: 200, // ~32 min coverage
    backoffMultiplier: 1.1, // Gentle slowdown
    maxIntervalMs: 10000, // Cap at 10 seconds
};
// =============================================================================
// WALLET BONUS (User-facing bonus percentage)
// =============================================================================
/**
 * GET /api/wallet/bonus - Response data
 * Simple endpoint returning current bonus percentage for deposit page
 */
export const walletBonusResponseDataSchema = z.object({
    /** Current bonus percentage (0-100) */
    bonusPercentage: z.number().int().min(0).max(100),
    /** Whether first-time bonus is enabled */
    firstTimeBonusEnabled: z.boolean(),
});
export const walletBonusResponseSchema = successResponseSchema(walletBonusResponseDataSchema);
/**
 * Default bonus polling interval (1 minute)
 * Used by frontend to poll for bonus changes while on deposit page
 */
export const BONUS_POLLING_INTERVAL_MS = 60000;
//# sourceMappingURL=deposit.js.map